import React, {
  Dispatch,
  SetStateAction,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useRouter } from 'next/navigation';

import CustomIcon from '@components/utils/CustomIcon';
import {
  DifferenceIcon,
  RemoveDiffItem,
  SelectedDifferenceIcon,
} from '@assets/icons';
import Tooltip from '@components/ui/Tooltip/next';
import palette from '@utils/theme/palette';
import {
  HotelDetailViewModel,
  HotelFeatureViewModel,
  HotelViewModel,
} from '@api/v3';
import { useOutsideClick } from '@hooks/hooks';
import Typography from '@components/Typography';
import CloudinaryItemImage from '@components/ui/listing/CloudinaryItemImage';
import OriginalPrice from '@components/ui/price/OriginalPrice';
import {
  formatArrayToString,
  getCurrencySymbol,
  getNightCount,
} from '@utils/utils';
import { useQueryParams } from '@hooks/useQueryParams';
import {
  DEFAULT_DATE_FORMAT,
  HOTEL_SEARCH_DATE_FORMAT,
  convertStringDateFormat,
} from '@utils/dateUtil';
import { convertQueryRoomToHotelRooms } from '@components/views/hotel/utils';
import {
  prepareDetailCategories,
  prepareListingCategories,
} from '@components/views/Hotel-comparison/utils';
import useGetasPath from '@hooks/useGetasPath';
import { isBetween } from '@components/views/hotel/detail/components/hotel-features/isBetween';
import {
  logAddToComparison,
  logProductComparison,
} from '@utils/gtm/hotel-comparison-gtm';

import {
  ButtonContainer,
  CloseIconContainer,
  ColCustomIcon,
  DesktopInfoMessageContainer,
  DiffHotelButton,
  DiffItemBody,
  DiffItemContainer,
  DiffItemNameWrapper,
  ListContainer,
  PriceContainer,
  Separator,
} from './styled';

const DesktopDifference = ({
  differenceItem,
  addDiffData,
  removeDiffData,
  diffData,
  isListing,
  setIsRedirecting,
}: {
  differenceItem: HotelViewModel | HotelDetailViewModel;
  diffData: any[];
  setDiffData: Dispatch<SetStateAction<any[]>>;
  addDiffData: (item: any) => void;
  removeDiffData: (index: number) => void;
  isListing: boolean;
  setIsRedirecting?: (isRedirecting: boolean) => void;
}) => {
  const IS_PROD = process.env.NEXT_PUBLIC_BUILD_TYPE === 'PRODUCTION';

  const [visible, setVisible] = useState(false);
  const [onMouse, setOnMouse] = useState(false);
  const [infoType, setInfoType] = useState<
    'MAX_LIMIT' | 'ALREADY_ADDED' | 'ADD_SUCCESS' | 'REMOVE_SUCCESS' | ''
  >('');
  const query = useQueryParams();
  const asPath = useGetasPath();
  const router = useRouter();
  const infoMessages = {
    MAX_LIMIT: 'Karşılaştırma listenize en fazla 3 otel ekleyebilirsiniz.',
    ALREADY_ADDED: 'Otel karşılaştırma listenizde bulunmaktadır.',
    ADD_SUCCESS: 'Otel karşılaştırma listenize eklendi.',
    REMOVE_SUCCESS: '· Otel karşılaştırma listenizden çıkarıldı.',
  };
  const ref = useRef(null);

  const differenceFeatures = useMemo(() => {
    if (isListing) {
      return prepareListingCategories(differenceItem, IS_PROD);
    } else {
      const mappedDifferenceItem = differenceItem as HotelDetailViewModel;
      const summer = mappedDifferenceItem?.seasonFeatures?.find(
        (item) => item.name === 'Summer'
      );
      const winter = mappedDifferenceItem?.seasonFeatures?.find(
        (item) => item.name === 'Winter'
      );
      const noSeason = mappedDifferenceItem?.seasonFeatures?.find(
        (item) => item.name === 'NoSeason'
      );

      if (query.in && query.out) {
        const beginDate = new Date(formatArrayToString(query.in || ''));
        const summerStart = new Date(
          formatArrayToString(summer?.startDate || '')
        );
        const summerEnd = new Date(formatArrayToString(summer?.endDate || ''));
        const winterStart = new Date(
          formatArrayToString(winter?.startDate || '')
        );
        const winterEnd = new Date(formatArrayToString(winter?.endDate || ''));

        if (beginDate) {
          const sessionSummer = isBetween({
            beginDate: beginDate as Date,
            start: summerStart,
            end: summerEnd,
          });

          const sessionWinter = isBetween({
            beginDate: beginDate as Date,
            start: winterStart,
            end: winterEnd,
          });

          if (sessionSummer) {
            const allFeatures = summer?.categories?.reduce(
              (acc: HotelFeatureViewModel[], item) => {
                return acc?.concat(item.features);
              },
              []
            );
            return prepareDetailCategories(allFeatures, IS_PROD);
          } else if (sessionWinter) {
            const allFeatures = winter?.categories?.reduce(
              (acc: HotelFeatureViewModel[], item) => {
                return acc?.concat(item.features);
              },
              []
            );
            return prepareDetailCategories(allFeatures, IS_PROD);
          } else if (!sessionWinter && !sessionSummer) {
            const allFeatures = noSeason?.categories?.reduce(
              (acc: HotelFeatureViewModel[], item) => {
                return acc.concat(item?.features);
              },
              []
            );
            return prepareDetailCategories(allFeatures, IS_PROD);
          }
        }
      } else {
        if (summer) {
          const allFeatures = summer?.categories?.reduce(
            (acc: HotelFeatureViewModel[], item) => {
              return acc?.concat(item?.features);
            },
            []
          );
          return prepareDetailCategories(allFeatures, IS_PROD);
        } else if (winter) {
          const allFeatures = winter?.categories?.reduce(
            (acc: HotelFeatureViewModel[], item) => {
              return acc?.concat(item?.features);
            },
            []
          );
          return prepareDetailCategories(allFeatures, IS_PROD);
        } else if (noSeason) {
          const allFeatures = noSeason?.categories?.reduce(
            (acc: HotelFeatureViewModel[], item) => {
              return acc?.concat(item?.features);
            },
            []
          );
          return prepareDetailCategories(allFeatures, IS_PROD);
        }
      }
    }
  }, [IS_PROD, differenceItem, isListing, query.in, query.out]);

  useOutsideClick(ref, () => {
    setInfoType('');
    setVisible(false);
  });

  const parseDateToISO = (dateString: string) => {
    const [day, month, year] = dateString.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toISOString();
  };

  const addToDifferenceList = () => {
    setOnMouse(false);
    setVisible(true);
    if (diffData?.some((item) => item?.hotelId === differenceItem?.id)) {
      setInfoType('ALREADY_ADDED');
      return;
    }
    if (diffData?.length === 3) {
      setInfoType('MAX_LIMIT');
      return;
    }
    setInfoType('ADD_SUCCESS');
    logAddToComparison(
      differenceItem?.name ?? '',
      differenceItem?.id.toString(),
      'Otel'
    );

    const rooms = convertQueryRoomToHotelRooms(query.room as string);
    const adultNumber = rooms.reduce((t, a) => t + a.adultCount, 0);
    const childrenCount = rooms.reduce((t, a) => t + a.childrenList.length, 0);
    const guestCount = adultNumber + childrenCount;
    const roomCount = rooms.length;
    const nightCount =
      query.in && query.out ? getNightCount(query.in, query.out) : 0;
    const roomSelectBoxLabel = (() => {
      if (guestCount <= 0) return '';

      if (query.in && query.out) {
        return `${roomCount} Oda, ${nightCount} Gece, ${guestCount} Kişi`;
      }

      return `${roomCount} Oda, ${guestCount} Kişi`;
    })();

    if (isListing) {
      addDiffData({
        hotelId: differenceItem?.id,
        name: differenceItem?.name,
        image: differenceItem.images?.[0]?.url,
        price: (differenceItem as HotelViewModel)?.pricing?.discounted,
        currency: (differenceItem as HotelViewModel)?.pricing?.currency,
        dates: {
          startDate: query.in
            ? parseDateToISO(
                convertStringDateFormat(
                  query.in as string,
                  HOTEL_SEARCH_DATE_FORMAT,
                  DEFAULT_DATE_FORMAT
                ).replaceAll('.', '/')
              )
            : null,
          endDate: query.out
            ? parseDateToISO(
                convertStringDateFormat(
                  query.out as string,
                  HOTEL_SEARCH_DATE_FORMAT,
                  DEFAULT_DATE_FORMAT
                ).replaceAll('.', '/')
              )
            : null,
        },
        roomGuestLabel: roomSelectBoxLabel,
        url: (differenceItem as HotelViewModel)?.url,
        originalPrice: (differenceItem as HotelViewModel)?.pricing?.original,
        categories: differenceFeatures,
      });
    } else {
      addDiffData({
        hotelId: differenceItem?.id,
        name: differenceItem?.name,
        image: (differenceItem as HotelDetailViewModel)?.images?.[0]?.url,
        price: (differenceItem as HotelDetailViewModel)?.lowestPrice?.price,
        currency: (differenceItem as HotelDetailViewModel)?.lowestPrice
          ?.currencyCode,
        dates: {
          startDate: query.in
            ? parseDateToISO(
                convertStringDateFormat(
                  query.in as string,
                  HOTEL_SEARCH_DATE_FORMAT,
                  DEFAULT_DATE_FORMAT
                ).replaceAll('.', '/')
              )
            : null,
          endDate: query.out
            ? parseDateToISO(
                convertStringDateFormat(
                  query.out as string,
                  HOTEL_SEARCH_DATE_FORMAT,
                  DEFAULT_DATE_FORMAT
                ).replaceAll('.', '/')
              )
            : null,
        },
        roomGuestLabel: roomSelectBoxLabel,
        url: asPath,
        originalPrice: (differenceItem as HotelDetailViewModel)?.lowestPrice
          ?.originalPrice,
        categories: differenceFeatures,
      });
    }
  };

  const removeFromDifferenceList = (itemId) => {
    setInfoType('REMOVE_SUCCESS');
    removeDiffData(itemId);
    if (diffData?.length === 1) {
      setVisible(false);
    }
  };
  const handleComparisonClick = () => {
    const hotelsName = diffData.map((item) => `${item.name}`).join('-');
    const hotelsId = diffData.map((item) => `${item.hotelId}`).join('-');
    logProductComparison(
      hotelsName,
      hotelsId,
      Array(diffData.length).fill('Otel').join('-')
    );
    setIsRedirecting?.(true);
    router.push('/otel-karsilastir');
  };
  const addedList = diffData?.some(
    (item) => item?.hotelId === differenceItem?.id
  );
  return (
    <Tooltip
      getPopupContainer={(triggerNode) => triggerNode}
      trigger='hover'
      borderRadius='8px'
      overlayInnerStyle={{
        borderRadius: '8px',
        border: 'none',
        backgroundColor: palette.white,
        width: !onMouse ? '412px' : '180px',
        padding: !onMouse ? '16px 12px' : '8px',
        textAlign: !onMouse ? 'left' : 'center',
      }}
      placement='bottomRight'
      arrowColor={palette.white}
      open={visible || (onMouse && !addedList)}
      zIndex={1}
      title={
        <div>
          {onMouse && !addedList ? (
            <Typography color='NL800' variant='caption3' weight='Regular'>
              Otel karşılaştırması yapabilmek için listenize otel ekleyiniz.
            </Typography>
          ) : (
            <>
              {infoType ? (
                <DesktopInfoMessageContainer
                  backgroundColor={
                    infoType === 'ADD_SUCCESS' || infoType === 'REMOVE_SUCCESS'
                      ? '#E0F7DC'
                      : infoType === 'ALREADY_ADDED'
                      ? '#E8F1FC'
                      : infoType === 'MAX_LIMIT'
                      ? '#FEF4EB'
                      : 'transparent'
                  }
                >
                  <Typography
                    color='NL800'
                    variant='caption3'
                    weight='SemiBold'
                    as='div'
                  >
                    {infoMessages[infoType]}
                  </Typography>
                  {infoType !== 'MAX_LIMIT' ? (
                    <Typography
                      color='NL800'
                      variant='caption3'
                      weight='Regular'
                      as='div'
                    >
                      Karşılaştırma listenize en fazla 3 otel ekleyebilirsiniz.
                    </Typography>
                  ) : null}
                </DesktopInfoMessageContainer>
              ) : null}
              <ListContainer>
                {diffData?.map((item, index) => (
                  <>
                    <DiffItemContainer key={index}>
                      <CloudinaryItemImage
                        url={item?.image ?? 'setur'}
                        alt={item?.name || 'setur'}
                        key={item?.imageUrl}
                        fallBackImage='/static/images/setur-static.jpg'
                        width={120}
                        height={80}
                        wrapperWidth='120px'
                      />
                      <DiffItemBody>
                        <DiffItemNameWrapper data-testid='difference-item-name-wrapper'>
                          <Typography
                            color='D400'
                            variant='caption1'
                            weight='Regular'
                            style={{ width: '234px', lineHeight: '24px' }}
                          >
                            {item?.name}
                          </Typography>
                          <CloseIconContainer
                            data-testid='remove-hotel-from-difference'
                            onClick={() =>
                              removeFromDifferenceList(item?.hotelId)
                            }
                          >
                            <CustomIcon icon={RemoveDiffItem} fontSize={15} />
                          </CloseIconContainer>
                        </DiffItemNameWrapper>
                        {item?.price ? (
                          <PriceContainer>
                            <OriginalPrice
                              price={+item?.price}
                              currency={getCurrencySymbol(item?.currency)}
                              noLine={false}
                              size={16}
                              color='D400'
                              height='24px'
                              control={true}
                            />
                          </PriceContainer>
                        ) : null}
                      </DiffItemBody>
                    </DiffItemContainer>
                    {index !== diffData.length - 1 ? <Separator /> : null}
                  </>
                ))}
              </ListContainer>
              {diffData.length > 1 ? (
                <ButtonContainer>
                  <DiffHotelButton
                    data-testid='take-hotel-difference-button-inside-tooltip'
                    onClick={() => {
                      handleComparisonClick();
                    }}
                  >
                    <Typography color='NL0' variant='caption2' weight='Bold'>
                      Karşılaştır
                    </Typography>
                  </DiffHotelButton>
                </ButtonContainer>
              ) : null}
            </>
          )}
        </div>
      }
    >
      <ColCustomIcon
        ref={ref}
        onClick={addToDifferenceList}
        onMouseEnter={() => (visible ? setOnMouse(false) : setOnMouse(true))}
        onMouseLeave={() => setOnMouse(false)}
        data-testid='add-hotel-to-difference'
      >
        <CustomIcon
          icon={
            diffData?.some((item) => item?.hotelId === differenceItem?.id)
              ? SelectedDifferenceIcon
              : DifferenceIcon
          }
          fontSize={24}
        />
      </ColCustomIcon>
    </Tooltip>
  );
};

export default DesktopDifference;
